<template>
    <div class="fc-container rounded has-border px-2 py-0">
        <div class="fc-title">
            <e-tooltip :label="title" multilined append-to-body>
                <div class="fc-title-text">
                    {{ title }}
                </div>
            </e-tooltip>
        </div>
        <div class="fc-actions">
            <div class="is-flex is-justify-content-end is-align-items-center">
                <div class="has-text-weight-semibold has-text-grey-light mr-2 truncate">{{ fileSize }}</div>
                <div class="is-flex is-justify-content-end is-align-items-center">
                    <a
                        v-if="!hideDownload && downloadHref"
                        :href="downloadHref"
                        :download="title"
                        class="is-flex is-justify-content-center is-align-items-center"
                    >
                        <span class="icon is-size-6 icon-fixed-margin">
                            <i
                                :class="[
                                    'uil',
                                    [downloadStatus === 'downloaded' ? 'uil-check-circle' : 'uil-download-alt'],
                                    [downloadStatus === 'downloading' ? 'uil-spinner' : 'uil-download-alt'],
                                ]"
                            />
                        </span>
                    </a>
                    <a
                        v-if="canClose"
                        href="#"
                        class="is-flex is-justify-content-center is-align-items-center"
                        @click.prevent="$emit('delete', file)"
                    >
                        <span class="icon is-size-4 icon-fixed-margin">
                            <i class="uil uil-times" />
                        </span>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import prettyBytes from 'pretty-bytes';
import { computed } from 'vue';

import { resolveMediaDownloadHref } from '@/utilities/mediaDownloadHref.js';

const props = defineProps({
    file: {
        type: [Object, File],
        required: true,
    },
    label: {
        type: String,
        required: false,
        default: 'Attachments',
    },
    canClose: {
        type: Boolean,
        default: false,
    },
    downloadStatus: {
        type: String,
        required: false,
        default: 'none',
    },
    hideDownload: {
        type: Boolean,
        default: false,
    },
});

defineEmits(['delete']);

const title = computed(() => {
    return props.file.name || props.file.title || props.file.originalName || props.file.fileName || 'File';
});

const fileSize = computed(() => {
    return props.file.size ? prettyBytes(props.file.size) : 'Unknown Size';
});

const downloadHref = computed(() => resolveMediaDownloadHref(props.file));
</script>

<style scoped>
.b-tooltip {
    display: ruby !important;
}

.truncate {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.fixed-margin {
    margin-bottom: -9px;
}

.icon-fixed-margin {
    margin-bottom: 3px;
}

.fc-container {
    container-type: inline-size;
    container-name: file-chip;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    grid-template-rows: auto;
    grid-template-areas: 't t t t a';
    column-gap: 10px;
    justify-items: stretch;
    align-items: center;
    justify-content: stretch;
    align-content: stretch;
}
.fc-title {
    grid-area: t;
    min-width: 0;
}

.fc-title-text {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

@container file-chip (max-width: 380px) {
    .fc-title-text {
        display: -webkit-box;
        overflow: hidden;
        overflow-wrap: anywhere;
        word-break: break-word;
        white-space: normal;
        line-height: 1.25;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        line-clamp: 2;
    }
}
.fc-size {
    grid-area: s;
    justify-self: end;
}
.fc-actions {
    grid-area: a;
    justify-self: end;
}

@media (max-width: 600px) {
    .fc-container {
        grid-template-columns: repeat(3, 1fr);
        grid-template-areas:
            't t t t t'
            's s a a a';
    }

    .fc-size {
        justify-self: start;
    }
}
</style>
